import React, { useMemo, useState, useEffect } from "react";
import * as Yup from "yup";
import { Button, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Formik, Form } from "formik";
import PageTitle from "../components/items/page-title";
import Table from "../components/tables/Table";
import { PaginationType } from "../components/tables/types";
import { PAGINATION } from "../types/global";
import Loading from "../components/Loading";
import { useClonePairsMutation, useLazyGetPairsQuery } from "../api/pairs";
import getColumnDefsPairs from "../utils/pairsColumn";
import { PairFilter } from "../models/Pair";
import { TypesOfInput } from "../types/IField";
import Field from "../components/form/formik/Field";
import { filterInitialValues, getFilterFields } from "../utils/pairFields";
import { useGetClientsQuery } from "../api/clients";
import { useGetCurrenciesQuery } from "../api/currencies";
import FilterForm from "../components/form/formik/FilterForm";

const Pairs = () => {
  const [pagination, setPagination] = useState<PaginationType>(PAGINATION);
  const [filter, setFilter] = useState<PairFilter>(filterInitialValues);
  const [selectedPairs, setSelectedPairs] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [clonePairs, { isLoading: isLoadingClone }] = useClonePairsMutation();

  const [fetch, query, { lastArg }] = useLazyGetPairsQuery();
  const { data, isLoading } = query;

  useEffect(() => {
    if (data) {
      setPagination({
        ...data.pagination,
        page: data.pagination.currentPage || 1,
      });
    }
  }, [data]);

  useEffect(() => {
    if (
      lastArg.pagination?.page === pagination.page &&
      lastArg.pagination?.take === pagination.take
    ) {
      return;
    }

    fetch({
      pagination: { take: pagination.take, page: pagination.page },
      filter,
    });
  }, [pagination]);

  useEffect(() => {
    fetch({
      pagination: { take: pagination.take, page: pagination.page },
      filter,
    });
  }, [filter]);

  const handleClonePairs = (values: { domain: string }) => {
    clonePairs({ ids: selectedPairs, domain: values.domain }).then(() => setShowModal(false));
  };

  const columns = useMemo(() => getColumnDefsPairs({ pagination }), [pagination]);

  const filterFields = useMemo(
    () =>
      getFilterFields({
        currenciesQuery: useGetCurrenciesQuery,
        clientsQuery: useGetClientsQuery,
      }),
    [useGetClientsQuery, useGetCurrenciesQuery],
  );

  const handleFilter = (values: PairFilter) => {
    setFilter(values);
    return false;
  };

  if (isLoading) return <Loading />;
  if (!data) return null;

  return (
    <div className="container-fluid px-lg-4 px-xl-5">
      <PageTitle title="Pairs" />
      <Link to="/pairs/new">
        <Button className="mb-3" variant="primary">
          Create pair
        </Button>
      </Link>
      <FilterForm
        className="mb-3"
        fields={filterFields}
        handleSubmit={handleFilter}
        initialValues={filterInitialValues}
      />
      {!!selectedPairs.length && (
        <Button disabled={isLoadingClone} onClick={() => setShowModal(true)} variant="primary">
          Clone {selectedPairs.length} pairs
        </Button>
      )}
      <div className="row">
        <section className="mb-5">
          <div className="card">
            <div className="card-body">
              <Table
                setPagination={setPagination}
                className="min-w-screen-lg"
                columns={columns}
                containerClass="overflow-auto"
                fixed
                items={data.list}
                pagination={pagination}
                dataKey="pairs"
                rowSelect
                handleSelect={setSelectedPairs}
              />
            </div>
          </div>
        </section>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Clone pairs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{
              domain: "",
            }}
            validationSchema={Yup.object().shape({
              domain: Yup.string().required("Required"),
            })}
            onSubmit={handleClonePairs}
          >
            <Form>
              <Field type={TypesOfInput.TEXT} name="domain" size={12} />
              <Button type="submit">Clone</Button>
            </Form>
          </Formik>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Pairs;
